import * as messageHelper from "../../helpers/messageHelper";
import * as methods from "../../constants/serverMethods";
import * as modalTypes from "../../constants/modalTypes";
import * as navigationActions from "../../actions/navigationActions";
import * as objectTypes from "../../constants/objectTypes";
import * as propertyConstants from "../../constants/propertyConstants";
import * as responseValidationHelper from "../../helpers/responseValidationHelper";
import * as universalObjectActions from "../../actions/universalObjectActions";
import * as websocketActions from "../../actions/websocketActions";

import { WebZone, mapWebZone } from "../../types/web/webZone";
import { Zone, mapZone } from "../../types/zone";

import { AppState } from "../../reducers";
import { Modal } from "../../types/modal";
import { ServerResponse } from "../../types/serverResponse";

export const processServerResponse = (method: string, response: ServerResponse, state: AppState): Array<any> => {
    switch (method) {
        case methods.METHOD_GET_ALL_ZONES_AVAILABLE_FOR_COMPANY_NO_PARAMS: {
            if (!responseValidationHelper.isArrayResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            const webZoneList: Array<WebZone> = [];
            let webZone: WebZone | null;

            for (const item of response.data.data) {
                webZone = mapWebZone(item);

                if (webZone) {
                    webZoneList.push(webZone);
                }
            }

            return [universalObjectActions.saveItemsNoParams(objectTypes.WEB_ZONE, webZoneList)];
        }
        case methods.METHOD_GET_ALL_ZONES_AVAILABLE_FOR_COMPANY: {
            if (!responseValidationHelper.isArrayResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            const webZoneList: Array<WebZone> = [];
            let webZone: WebZone | null;

            for (const item of response.data.data) {
                webZone = mapWebZone(item);

                if (webZone) {
                    webZoneList.push(webZone);
                }
            }

            return [universalObjectActions.saveItems(objectTypes.WEB_ZONE, webZoneList, response.data.count)];
        }
        case methods.METHOD_GET_ALL_DUPLICATE_ZONES_NO_PARAMS:
        case methods.METHOD_GET_ALL_ZONES_NO_PARAMS: {
            if (!responseValidationHelper.isArrayResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            const zoneList: Array<Zone> = [];
            const objectType =
                method === methods.METHOD_GET_ALL_DUPLICATE_ZONES_NO_PARAMS
                    ? objectTypes.ZONE_DUPLICATE
                    : objectTypes.ZONE;
            let zone: Zone | null;

            for (const item of response.data.data) {
                zone = mapZone(item);

                if (zone) {
                    zoneList.push(zone);
                }
            }

            return [universalObjectActions.saveItemsNoParams(objectType, zoneList)];
        }
        case methods.METHOD_GET_ALL_ZONES_BY_SYSTEM: {
            if (!responseValidationHelper.isArrayResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            const zoneList: Array<Zone> = [];
            let zone: Zone | null;

            for (const item of response.data.data) {
                zone = mapZone(item);

                if (zone) {
                    zoneList.push(zone);
                }
            }

            return [universalObjectActions.saveItems(objectTypes.ZONE, zoneList, response.data.count)];
        }
        case methods.METHOD_ADD_ZONE: {
            if (!responseValidationHelper.isResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                universalObjectActions.reloadData(objectTypes.DATABASE_SYSTEM_ZONE),
                universalObjectActions.reloadData(objectTypes.ZONE),
                universalObjectActions.getAllItems(objectTypes.ZONE, {
                    systemId: state.system.active?.[propertyConstants.PROPERTY_ID]
                }),
                universalObjectActions.getAllItems(objectTypes.ZONE_DUPLICATE, {
                    systemId: state.system.activeList?.[0][propertyConstants.PROPERTY_ID]
                }),
                navigationActions.navigationCloseModal(modalTypes.MODAL_ZONES_ADD)
            ];
        }
        case methods.METHOD_EDIT_ZONE: {
            if (!responseValidationHelper.isResponseValid(response)) {
                return [
                    messageHelper.getInvalidFormatMessage(response),
                    universalObjectActions.reloadData(objectTypes.DATABASE_SYSTEM_ZONE),
                    universalObjectActions.reloadData(objectTypes.ZONE),
                    universalObjectActions.getAllItems(objectTypes.ZONE, {
                        systemId: state.system.active?.[propertyConstants.PROPERTY_ID]
                    })
                ];
            }

            const requests = [universalObjectActions.dataChanged(true)];

            if (state.navigation.modalList.some((modal: Modal) => modal.type === modalTypes.MODAL_ZONES_EDIT)) {
                requests.push(universalObjectActions.reloadData(objectTypes.ZONE));
                requests.push(navigationActions.navigationCloseModal(modalTypes.MODAL_ZONES_EDIT));
                requests.push(
                    universalObjectActions.getAllItems(objectTypes.ZONE_DUPLICATE, {
                        systemId: state.system.activeList?.[0][propertyConstants.PROPERTY_ID]
                    })
                );
            } else {
                const zone = mapZone(response.data);

                if (zone) {
                    requests.push(universalObjectActions.editItemSuccess(objectTypes.ZONE, [zone]));
                }
            }

            return requests;
        }
        case methods.METHOD_DELETE_ZONE: {
            if (!responseValidationHelper.isResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                universalObjectActions.reloadData(objectTypes.DATABASE_SYSTEM_ZONE),
                universalObjectActions.reloadData(objectTypes.ZONE),
                universalObjectActions.getAllItems(objectTypes.ZONE, {
                    systemId: state.system.active?.[propertyConstants.PROPERTY_ID]
                }),
                universalObjectActions.getAllItems(objectTypes.ZONE_DUPLICATE, {
                    systemId: state.system.activeList?.[0][propertyConstants.PROPERTY_ID]
                }),
                universalObjectActions.setActiveItems(objectTypes.ZONE, [])
            ];
        }
        case methods.METHOD_DUPLICATE_ZONE: {
            if (!responseValidationHelper.isResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                universalObjectActions.reloadData(objectTypes.DATABASE_SYSTEM_ZONE),
                universalObjectActions.reloadData(objectTypes.ZONE),
                universalObjectActions.getAllItems(objectTypes.ZONE, {
                    systemId: state.system.active?.[propertyConstants.PROPERTY_ID]
                }),
                navigationActions.navigationCloseModal(modalTypes.MODAL_ZONES_DUPLICATE)
            ];
        }
        case methods.METHOD_SET_ACTIVE_ZONE: {
            if (!responseValidationHelper.isResponseValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [];
        }
        default: {
            return [websocketActions.serverUnknown()];
        }
    }
};
